/**
 * Index Oglasi embed loader.
 *
 * Built (minified) to `public/embed/index-oglasi-embed.min.js` by `npm run build:embed`.
 * Third party sites drop in:
 *
 *   <div class="index-oglasi-embed" data-index-oglasi-embed="ffff-gggg-hhhh"></div>
 *   <script async src="https://www.index.hr/oglasi/embed/index-oglasi-embed.min.js"></script>
 *
 * and get an iframe pointing at `<base>/embed/<id>`, which is served by EmbedController.
 *
 * No dependencies, and Babel compiles this down to ES5 - it runs on sites we do not control,
 * so it must not assume a bundler, a polyfill or a modern browser. It also must never throw
 * into the host page.
 *
 * The message type strings below are shared with `src/embed/bridge.ts`.
 */
(function () {
  const LOADED_FLAG = 'data-index-oglasi-embed-loaded';
  const SELECTOR = '.index-oglasi-embed[data-index-oglasi-embed]';
  // Ids are case sensitive slugs. The id goes straight into a URL, so keep this tight, and
  // keep it in step with EmbedController and src/embed/widgets/index.ts.
  const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
  // Optional presets a placement can carry. Both are validated again by EmbedController - the
  // check here only keeps a typo in a partner's CMS from turning into a pointless request.
  // Keep these in step with `LocationKeyPattern` and `PropertyTypePattern` there.
  const LOCATION_PATTERN =
    /^(?:county|city|settlement):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const PROPERTY_TYPE_PATTERN = /^[a-z0-9-]{1,64}$/;
  const DEFAULT_BASE = 'https://www.index.hr/oglasi';

  // Re-running the script tag on an already-initialised page just picks up new placeholders.
  if (window.IndexOglasiEmbed) {
    window.IndexOglasiEmbed.scan();
    return;
  }

  const base = resolveBase();
  const origin = base.match(/^https?:\/\/[^/]+/i)[0];
  const trustedOrigins = buildTrustedOrigins(origin);
  const frames = [];
  let hostIsDark = detectHostDarkTheme();

  window.IndexOglasiEmbed = {
    scan,
    /** Optional host hook: called with the widget id whenever someone clicks inside it. */
    onInteraction: null,
  };

  /**
   * Derives the app base from this script's own `src`, so the same file works on
   * production, dev2 and localhost without being rebuilt per environment.
   *
   * https://host/oglasi/embed/index-oglasi-embed.min.js -> https://host/oglasi
   */
  function resolveBase() {
    let script = document.currentScript;

    if (!script) {
      const scripts = document.getElementsByTagName('script');
      script = scripts[scripts.length - 1];
    }

    let resolved = DEFAULT_BASE;

    if (script && script.src) {
      const match = script.src.match(/^(https?:\/\/[^/]+(?:\/[^/]+)*?)\/embed\/[^/]*$/i);

      if (match) {
        resolved = match[1];
      }
    }

    // Never frame over plain HTTP - the host page is very likely https and would block it.
    return resolved.replace(/^http:/i, 'https:');
  }

  /** Origins we accept postMessage from: our own, plus the www/non-www sibling. */
  function buildTrustedOrigins(ownOrigin) {
    const origins = {};

    origins[ownOrigin] = true;
    origins[ownOrigin.replace('://www.', '://')] = true;
    origins[ownOrigin.replace(/^https:\/\/(?!www\.)/i, 'https://www.')] = true;

    return origins;
  }

  function detectHostDarkTheme() {
    try {
      return !!document.querySelector('html.dark-theme, body.dark-theme, body > .dark-theme');
    } catch (e) {
      return false;
    }
  }

  function findByWindow(source) {
    for (let i = 0; i < frames.length; i++) {
      if (frames[i].iframe.contentWindow === source) {
        return frames[i];
      }
    }

    return null;
  }

  /**
   * Appends `name=value` when the placeholder carries a preset we can use.
   *
   * A malformed value is dropped rather than forwarded. EmbedController would reject it
   * anyway, and a widget that ignores an unreadable preset and shows its ordinary empty form
   * is a better outcome on someone else's page than one that shows nothing.
   */
  function addParam(query, name, value, pattern) {
    if (value && pattern.test(value)) {
      query.push(`${name}=${encodeURIComponent(value)}`);
    }
  }

  function mount(placeholder) {
    if (placeholder.hasAttribute(LOADED_FLAG)) {
      return;
    }

    const publicId = placeholder.getAttribute('data-index-oglasi-embed');

    if (!publicId || !ID_PATTERN.test(publicId)) {
      return;
    }

    placeholder.setAttribute(LOADED_FLAG, '');

    const iframe = document.createElement('iframe');
    const query = [];

    if (hostIsDark) {
      query.push('theme=dark');
    }

    // Optional: the placement can open with a location and a property type already chosen.
    // A placement carrying both opens on its results; one on its own only fills a field in.
    addParam(
      query,
      'location',
      placeholder.getAttribute('data-index-oglasi-embed-location'),
      LOCATION_PATTERN
    );
    addParam(
      query,
      'propertyType',
      placeholder.getAttribute('data-index-oglasi-embed-property-type'),
      PROPERTY_TYPE_PATTERN
    );

    iframe.src = `${base}/embed/${encodeURIComponent(publicId)}${
      query.length ? `?${query.join('&')}` : ''
    }`;
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', 'Index Oglasi');
    // Height stays 0 until the widget reports its own size over postMessage.
    //
    // The iframe is taken out of the flow and the placeholder carries the layout height
    // instead. The two part company whenever the widget opens a dropdown: the iframe has to
    // grow to draw it, but growing the box the host lays out would shove the rest of the
    // article down every time someone clicks the location field. Positioned out of the flow,
    // the extra height overlays the text below rather than displacing it.
    iframe.style.cssText =
      'position:absolute;top:0;left:0;width:100%;border:0;display:block;overflow:hidden;height:0';

    // Only touched if it would otherwise trap the iframe - a partner may already be
    // positioning this element themselves.
    if (getComputedStyle(placeholder).position === 'static') {
      placeholder.style.position = 'relative';
    }

    placeholder.appendChild(iframe);
    // Case is preserved - ids are matched case sensitively by the widget registry.
    frames.push({ id: publicId, iframe, placeholder });
  }

  function scan(root) {
    const placeholders = (root || document).querySelectorAll(SELECTOR);

    for (let i = 0; i < placeholders.length; i++) {
      mount(placeholders[i]);
    }
  }

  function pushTheme() {
    const dark = detectHostDarkTheme();

    if (dark === hostIsDark) {
      return;
    }

    hostIsDark = dark;

    for (let i = 0; i < frames.length; i++) {
      try {
        frames[i].iframe.contentWindow.postMessage({ type: 'index-embed-theme', dark }, origin);
      } catch (e) {
        // Frame not ready yet; it reads the theme from its own query string on load.
      }
    }
  }

  window.addEventListener('message', (event) => {
    if (!trustedOrigins[event.origin] || !event.data) {
      return;
    }

    const frame = findByWindow(event.source);

    if (!frame) {
      return;
    }

    if (event.data.type === 'index-embed-resize') {
      const height = parseInt(event.data.height, 10);
      // Older widget bundles only ever sent `height`; treat that as "no overlay open".
      const frameHeight = parseInt(event.data.frameHeight, 10) || height;

      if (height > 0) {
        frame.placeholder.style.height = `${height}px`;
        frame.iframe.style.height = `${Math.max(frameHeight, height)}px`;
        // While the frame is taller than the space it reserves it is painting over the host's
        // own content, so it has to win the stacking order. Dropped again on the way back down
        // rather than left behind, where it would outrank a sticky header for no reason.
        frame.iframe.style.zIndex = frameHeight > height ? '2147483000' : '';
      }
    } else if (event.data.type === 'index-embed-interaction') {
      if (typeof window.IndexOglasiEmbed.onInteraction === 'function') {
        try {
          window.IndexOglasiEmbed.onInteraction(frame.id);
        } catch (e) {
          // A broken host callback must not break the widget.
        }
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scan();
    });
  } else {
    scan();
  }

  // Picks up placeholders injected after load (infinite scroll, SPA hosts, CMS widgets) and
  // host theme toggles.
  if (window.MutationObserver) {
    new MutationObserver(((mutations) => {
      let themeMayHaveChanged = false;

      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];

        if (mutation.type === 'attributes') {
          themeMayHaveChanged = true;
          continue;
        }

        for (let j = 0; j < mutation.addedNodes.length; j++) {
          const node = mutation.addedNodes[j];

          if (node.nodeType !== 1) {
            continue;
          }

          if (node.matches && node.matches(SELECTOR)) {
            mount(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }

      if (themeMayHaveChanged) {
        pushTheme();
      }
    })).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }
}());



// WEBPACK FOOTER //
// src/embed/loader.js